import {
  type HotelInventoryListItem,
  type HotelRoomOut,
  type UpdateHotelRoomRequest,
} from '@utazzo/shared';
import { UzBadge, UzButton, UzConfirmDialog, UzTooltip } from '@utazzo/ui';
import { motion } from 'framer-motion';
import {
  AlertTriangle,
  BedDouble,
  CalendarDays,
  ChevronRight,
  List,
  Pencil,
  Plus,
  Trash2,
  Users,
  Utensils,
} from 'lucide-react';
import React, { useMemo, useState } from 'react';

import {
  useCreateHotelInventory,
  useCreateHotelRoom,
  useDeleteHotelInventory,
  useDeleteHotelRoom,
  useUpdateHotelInventory,
  useUpdateHotelRoom,
} from '../use-hotels';

import { RateCardFormPanel } from './RateCardFormPanel';
import { RatePriceCalendar } from './RatePriceCalendar';
import { RoomFormDialog } from './RoomFormDialog';

/**
 * Rooms & Rates - the heart of the hotel editor.
 *
 * One screen for the natural hierarchy: each room is an expandable section
 * with its season rates nested inside. Adding a rate happens in the context
 * of a room (no disconnected dropdown picking), so the data can never end up
 * mis-keyed. Rates without a room link (legacy imports keyed only by room
 * type) collect in an "Unassigned rates" section at the bottom.
 */

interface RoomsRatesTabProps {
  hotelId: string;
  rooms: HotelRoomOut[];
  inventory: HotelInventoryListItem[];
  inventoryLoading: boolean;
  disabled: boolean;
}

const fmtPrice = (amount: number, currencyCode: string | null): string => {
  if (!Number.isFinite(amount)) return String(amount);
  const code = currencyCode ?? 'INR';
  const prefix = code === 'INR' ? '₹' : `${code} `;
  return `${prefix}${amount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
};

const fmtDate = (iso: string): string => {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' });
};

/** Short label for a weekday mask: All days / Weekdays / Weekends / custom. */
const weekdayLabel = (mask: number): string | null => {
  if (mask === 127) return null; // all days - no badge needed
  if (mask === 0b0111110) return 'Weekdays';
  if (mask === 0b1000001) return 'Weekends';
  const names = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  return [1, 2, 3, 4, 5, 6, 0]
    .filter((b) => (mask & (1 << b)) !== 0)
    .map((b) => names[b])
    .join(', ');
};

/** Headline price for a rate row: per-pax adult rate, falling back to fixed cost. */
const headlinePrice = (rate: HotelInventoryListItem): string => {
  if (rate.adultPrice > 0) {
    return `${fmtPrice(rate.adultPrice, rate.currencyCode)} / adult`;
  }
  if (rate.fixedRoomCost > 0) {
    return `${fmtPrice(rate.fixedRoomCost, rate.currencyCode)} / room`;
  }
  return 'No price set';
};

// ---------------------------------------------------------------------------
// Rate row - one line inside a room section
// ---------------------------------------------------------------------------

interface RateRowProps {
  rate: HotelInventoryListItem;
  onEdit: () => void;
  onDelete: () => void;
  disabled: boolean;
}

/** Compact "label price" chip for the breakdown line. Hidden when zero. */
const PriceChip = ({
  label,
  amount,
  currencyCode,
}: {
  label: string;
  amount: number;
  currencyCode: string | null;
}): React.JSX.Element | null => {
  if (!amount) return null;
  return (
    <span className="text-xs whitespace-nowrap text-zinc-500">
      {label}{' '}
      <span className="font-medium text-zinc-700 tabular-nums">
        {fmtPrice(amount, currencyCode)}
      </span>
    </span>
  );
};

const RateRow = ({ rate, onEdit, onDelete, disabled }: RateRowProps): React.JSX.Element => (
  <div className="group px-4 py-2.5 transition-colors hover:bg-zinc-50/60">
    <div className="flex items-center gap-3">
      <span className="flex w-36 shrink-0 flex-col text-xs text-zinc-600">
        <span className="tabular-nums">
          {fmtDate(rate.validityFrom)} - {fmtDate(rate.validityTo)}
        </span>
        {weekdayLabel(rate.weekdayMask) && (
          <span className="text-[11px] text-zinc-400">{weekdayLabel(rate.weekdayMask)}</span>
        )}
      </span>
      <span
        className={
          'w-32 shrink-0 text-sm font-medium tabular-nums ' +
          (headlinePrice(rate) === 'No price set' ? 'text-amber-600' : 'text-zinc-900')
        }
      >
        {headlinePrice(rate)}
      </span>
      {rate.blackoutDates.length > 0 && (
        <span className="hidden items-center gap-1 text-xs text-red-500 sm:flex">
          <span className="tabular-nums">{rate.blackoutDates.length} blackout</span>
        </span>
      )}
      <span className="flex-1" />
      <span className="inline-flex items-center gap-1.5 text-xs text-zinc-600">
        <span
          className={
            'h-1.5 w-1.5 rounded-full ' + (rate.isActive ? 'bg-emerald-500' : 'bg-zinc-400')
          }
          aria-hidden
        />
        {rate.isActive ? 'Active' : 'Inactive'}
      </span>
      <div className="flex items-center gap-px">
        <UzTooltip label="Edit rate">
          <UzButton
            variant="ghost"
            size="icon"
            onClick={onEdit}
            disabled={disabled}
            aria-label="Edit rate"
            className="h-7 w-7 text-zinc-400 hover:text-zinc-900"
          >
            <Pencil className="h-3.5 w-3.5" />
          </UzButton>
        </UzTooltip>
        <UzTooltip label="Delete rate">
          <UzButton
            variant="ghost"
            size="icon"
            onClick={onDelete}
            disabled={disabled}
            aria-label="Delete rate"
            className="h-7 w-7 text-zinc-400 hover:bg-red-50 hover:text-red-600"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </UzButton>
        </UzTooltip>
      </div>
    </div>
    {/* Full price matrix line - every component of the rate at a glance. */}
    <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5">
      <PriceChip
        label="Extra adult"
        amount={rate.extraAdultPrice}
        currencyCode={rate.currencyCode}
      />
      <PriceChip
        label="Extra child"
        amount={rate.extraChildPrice}
        currencyCode={rate.currencyCode}
      />
      <PriceChip
        label="Child + bed"
        amount={rate.childWithBedPrice}
        currencyCode={rate.currencyCode}
      />
      <PriceChip
        label="Child no bed"
        amount={rate.childWithoutBedPrice}
        currencyCode={rate.currencyCode}
      />
      <PriceChip label="Infant" amount={rate.infantPrice} currencyCode={rate.currencyCode} />
      {rate.adultPrice > 0 && rate.fixedRoomCost > 0 && (
        <PriceChip
          label="Fixed room"
          amount={rate.fixedRoomCost}
          currencyCode={rate.currencyCode}
        />
      )}
      {rate.mealOptions.length > 0 && (
        <span className="inline-flex items-center gap-1 text-xs whitespace-nowrap text-zinc-500">
          <Utensils className="h-3 w-3 text-zinc-400" aria-hidden />
          {rate.mealOptions
            .map((m) => m.mealPlanCode ?? m.mealPlanName)
            .filter(Boolean)
            .join(', ')}
        </span>
      )}
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Main tab
// ---------------------------------------------------------------------------

export const RoomsRatesTab = ({
  hotelId,
  rooms,
  inventory,
  inventoryLoading,
  disabled,
}: RoomsRatesTabProps): React.JSX.Element => {
  // Room CRUD
  const createRoom = useCreateHotelRoom(hotelId);
  const updateRoom = useUpdateHotelRoom(hotelId);
  const deleteRoom = useDeleteHotelRoom(hotelId);
  const [roomDialog, setRoomDialog] = useState<'create' | HotelRoomOut | null>(null);
  const [roomDelete, setRoomDelete] = useState<HotelRoomOut | null>(null);

  // Rate CRUD
  const createRate = useCreateHotelInventory(hotelId);
  const updateRate = useUpdateHotelInventory(hotelId);
  const deleteRate = useDeleteHotelInventory(hotelId);
  /** Which room has the inline rate form open; 'unassigned' for the bottom section. */
  const [rateFormRoom, setRateFormRoom] = useState<string | null>(null);
  const [rateEditing, setRateEditing] = useState<HotelInventoryListItem | null>(null);
  const [rateDelete, setRateDelete] = useState<HotelInventoryListItem | null>(null);
  const [calendarRooms, setCalendarRooms] = useState<Set<string>>(() => new Set());
  const toggleCalendar = (id: string): void =>
    setCalendarRooms((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const pending =
    disabled ||
    createRoom.isPending ||
    updateRoom.isPending ||
    deleteRoom.isPending ||
    createRate.isPending ||
    updateRate.isPending ||
    deleteRate.isPending;

  // Group rates by room; null hotelRoomId goes to 'unassigned'.
  const ratesByRoom = useMemo(() => {
    const map = new Map<string, HotelInventoryListItem[]>();
    for (const r of inventory) {
      const key = r.hotelRoomId ?? 'unassigned';
      const list = map.get(key) ?? [];
      list.push(r);
      map.set(key, list);
    }
    for (const list of map.values()) {
      list.sort((a, b) => a.validityFrom.localeCompare(b.validityFrom));
    }
    return map;
  }, [inventory]);
  const unassigned = ratesByRoom.get('unassigned') ?? [];

  // Expansion - all expanded by default for <=4 rooms, else first only.
  const [collapsed, setCollapsed] = useState<Set<string>>(
    () => new Set(rooms.length > 4 ? rooms.slice(1).map((r) => r.id) : []),
  );
  const toggle = (id: string): void =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const openRateForm = (roomKey: string, rate: HotelInventoryListItem | null): void => {
    setRateFormRoom(roomKey);
    setRateEditing(rate);
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.delete(roomKey);
      return next;
    });
  };
  const closeRateForm = (): void => {
    setRateFormRoom(null);
    setRateEditing(null);
  };

  const submitRate = async (
    payload: Parameters<typeof createRate.mutateAsync>[0],
  ): Promise<void> => {
    if (rateEditing) {
      await updateRate.mutateAsync({ inventoryId: rateEditing.id, payload });
    } else {
      await createRate.mutateAsync(payload);
    }
    closeRateForm();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h3 className="text-sm font-semibold tracking-tight text-zinc-900">Rooms & prices</h3>
          <p className="mt-0.5 text-xs leading-relaxed text-zinc-500">
            Each room holds its price periods - rates, extra beds and meals all live in the period.
            A room without prices can't go into an itinerary.
          </p>
        </div>
        <UzButton size="sm" onClick={() => setRoomDialog('create')} disabled={pending}>
          <Plus className="mr-1.5 h-3.5 w-3.5" />
          Add room
        </UzButton>
      </div>

      {/* Empty state */}
      {rooms.length === 0 && !inventoryLoading && (
        <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-zinc-300 bg-zinc-50/50 p-12 text-center">
          <BedDouble className="h-8 w-8 text-zinc-300" strokeWidth={1.5} aria-hidden />
          <div>
            <h4 className="text-sm font-semibold text-zinc-900">No rooms yet</h4>
            <p className="mx-auto mt-1 max-w-sm text-xs leading-relaxed text-zinc-500">
              Add the room types this hotel offers (Deluxe, Suite, Pool Villa...), then add season
              rates inside each room.
            </p>
          </div>
          <UzButton size="sm" onClick={() => setRoomDialog('create')} disabled={pending}>
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            Add the first room
          </UzButton>
        </div>
      )}

      {/* Room sections */}
      <motion.div layout className="space-y-3">
        {rooms.map((room) => {
          const rates = ratesByRoom.get(room.id) ?? [];
          const isCollapsed = collapsed.has(room.id);
          const formOpenHere = rateFormRoom === room.id;
          return (
            <section
              key={room.id}
              className="overflow-hidden rounded-lg border border-zinc-200 bg-white"
            >
              {/* Room header - the expand toggle is its own button so the
                  row actions are real siblings, not nested interactives. */}
              <div className="flex items-center gap-1 px-4 py-3">
                <button
                  type="button"
                  onClick={() => toggle(room.id)}
                  aria-expanded={!isCollapsed}
                  aria-label={`${isCollapsed ? 'Expand' : 'Collapse'} ${room.name}`}
                  className="-mx-2 flex min-w-0 flex-1 items-center gap-3 rounded px-2 py-1 text-left transition-colors hover:bg-zinc-50"
                >
                  <ChevronRight
                    className={
                      'h-4 w-4 shrink-0 text-zinc-400 transition-transform duration-150 ' +
                      (isCollapsed ? '' : 'rotate-90')
                    }
                    aria-hidden
                  />
                  <span className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1">
                    <span className="text-sm font-semibold tracking-tight text-zinc-900">
                      {room.name}
                    </span>
                    {room.roomTypeName && (
                      <span className="text-xs text-zinc-500">{room.roomTypeName}</span>
                    )}
                    <span className="inline-flex items-center gap-1 text-xs text-zinc-500">
                      <Users className="h-3 w-3 text-zinc-400" aria-hidden />
                      {room.minOccupancy === room.maxOccupancy
                        ? `${room.minOccupancy} pax`
                        : `${room.minOccupancy}-${room.maxOccupancy} pax`}
                    </span>
                    {!room.isActive && <UzBadge tone="secondary">Inactive</UzBadge>}
                    {rates.length === 0 && !inventoryLoading && (
                      <span className="inline-flex items-center gap-1 rounded bg-amber-50 px-1.5 py-0.5 text-[11px] font-medium text-amber-700">
                        <AlertTriangle className="h-3 w-3" aria-hidden />
                        No prices
                      </span>
                    )}
                  </span>
                  <span className="text-xs text-zinc-400 tabular-nums">
                    {rates.length > 0 && `${rates.length} period${rates.length === 1 ? '' : 's'}`}
                  </span>
                </button>
                <div className="flex shrink-0 items-center gap-px">
                  {rates.length > 0 && (
                    <UzTooltip label={calendarRooms.has(room.id) ? 'List view' : 'Calendar view'}>
                      <UzButton
                        variant="ghost"
                        size="icon"
                        onClick={() => toggleCalendar(room.id)}
                        disabled={pending}
                        aria-label="Toggle calendar view"
                        className="h-7 w-7 text-zinc-400 hover:text-zinc-900"
                      >
                        {calendarRooms.has(room.id) ? (
                          <List className="h-3.5 w-3.5" />
                        ) : (
                          <CalendarDays className="h-3.5 w-3.5" />
                        )}
                      </UzButton>
                    </UzTooltip>
                  )}
                  <UzButton
                    variant="ghost"
                    size="sm"
                    onClick={() => openRateForm(room.id, null)}
                    disabled={pending}
                    className="h-7 text-xs text-zinc-600 hover:text-zinc-900"
                  >
                    <Plus className="mr-1 h-3 w-3" />
                    Add price
                  </UzButton>
                  <UzTooltip label="Edit room">
                    <UzButton
                      variant="ghost"
                      size="icon"
                      onClick={() => setRoomDialog(room)}
                      disabled={pending}
                      aria-label={`Edit ${room.name}`}
                      className="h-7 w-7 text-zinc-400 hover:text-zinc-900"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </UzButton>
                  </UzTooltip>
                  <UzTooltip label="Delete room">
                    <UzButton
                      variant="ghost"
                      size="icon"
                      onClick={() => setRoomDelete(room)}
                      disabled={pending}
                      aria-label={`Delete ${room.name}`}
                      className="h-7 w-7 text-zinc-400 hover:bg-red-50 hover:text-red-600"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </UzButton>
                  </UzTooltip>
                </div>
              </div>

              {/* Rates */}
              {!isCollapsed && (
                <div className="border-t border-zinc-100">
                  {inventoryLoading ? (
                    <div className="space-y-2 p-4">
                      <div className="h-8 animate-pulse rounded bg-zinc-50" />
                      <div className="h-8 animate-pulse rounded bg-zinc-50" />
                    </div>
                  ) : rates.length === 0 && !formOpenHere ? (
                    <div className="flex items-center justify-between gap-3 px-4 py-3">
                      <p className="text-xs text-zinc-500">
                        No price periods yet. Add one so this room can be priced.
                      </p>
                      <UzButton
                        variant="outline"
                        size="sm"
                        onClick={() => openRateForm(room.id, null)}
                        disabled={pending}
                        className="h-7 text-xs"
                      >
                        <Plus className="mr-1 h-3 w-3" />
                        Add price
                      </UzButton>
                    </div>
                  ) : calendarRooms.has(room.id) ? (
                    <div className="p-4">
                      <RatePriceCalendar periods={rates} />
                    </div>
                  ) : (
                    <div className="divide-y divide-zinc-100">
                      {rates.map((rate) => (
                        <RateRow
                          key={rate.id}
                          rate={rate}
                          onEdit={() => openRateForm(room.id, rate)}
                          onDelete={() => setRateDelete(rate)}
                          disabled={pending}
                        />
                      ))}
                    </div>
                  )}

                  {formOpenHere && (
                    <div className="border-t border-zinc-100 bg-zinc-50/40 p-4">
                      <RateCardFormPanel
                        key={rateEditing?.id ?? `new-${room.id}`}
                        hotelId={hotelId}
                        roomScope={{ id: room.id, name: room.name }}
                        initial={rateEditing}
                        pending={pending}
                        onSubmit={submitRate}
                        onCancel={closeRateForm}
                      />
                    </div>
                  )}
                </div>
              )}
            </section>
          );
        })}
      </motion.div>

      {/* Unassigned rates (legacy imports keyed by room type only) */}
      {unassigned.length > 0 && (
        <section className="overflow-hidden rounded-lg border border-zinc-200 bg-white">
          <div className="flex items-center gap-2 border-b border-zinc-100 px-4 py-3">
            <span className="text-sm font-semibold tracking-tight text-zinc-900">
              Unassigned rates
            </span>
            <span className="text-xs text-zinc-500">
              Not linked to a room - edit each rate to attach it.
            </span>
            <span className="flex-1" />
            <span className="text-xs text-zinc-400 tabular-nums">{unassigned.length}</span>
          </div>
          <div className="divide-y divide-zinc-100">
            {unassigned.map((rate) => (
              <div key={rate.id}>
                <RateRow
                  rate={rate}
                  onEdit={() => openRateForm('unassigned', rate)}
                  onDelete={() => setRateDelete(rate)}
                  disabled={pending}
                />
                {rateFormRoom === 'unassigned' && rateEditing?.id === rate.id && (
                  <div className="border-t border-zinc-100 bg-zinc-50/40 p-4">
                    <RateCardFormPanel
                      key={rateEditing.id}
                      hotelId={hotelId}
                      initial={rateEditing}
                      pending={pending}
                      onSubmit={submitRate}
                      onCancel={closeRateForm}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Room dialogs */}
      {roomDialog === 'create' && (
        <RoomFormDialog
          mode="create"
          onClose={() => setRoomDialog(null)}
          onSubmit={async (values) => {
            await createRoom.mutateAsync(values);
            setRoomDialog(null);
          }}
          pending={createRoom.isPending}
        />
      )}
      {roomDialog !== null && roomDialog !== 'create' && (
        <RoomFormDialog
          mode="edit"
          room={roomDialog}
          onClose={() => setRoomDialog(null)}
          onSubmit={async (values) => {
            const payload: UpdateHotelRoomRequest = values;
            await updateRoom.mutateAsync({ roomId: roomDialog.id, payload });
            setRoomDialog(null);
          }}
          pending={updateRoom.isPending}
        />
      )}

      {/* Delete confirms */}
      <UzConfirmDialog
        open={Boolean(roomDelete)}
        onOpenChange={(o) => {
          if (!o) setRoomDelete(null);
        }}
        title="Delete this room type?"
        description={
          roomDelete
            ? `"${roomDelete.name}" will be soft-deleted and hidden from lists. Itinerary components using it stay intact.`
            : ''
        }
        confirmLabel="Delete"
        tone="destructive"
        onConfirm={async () => {
          if (!roomDelete) return;
          await deleteRoom.mutateAsync(roomDelete.id);
          setRoomDelete(null);
        }}
      />
      <UzConfirmDialog
        open={Boolean(rateDelete)}
        onOpenChange={(o) => {
          if (!o) setRateDelete(null);
        }}
        title="Delete this rate?"
        description={
          rateDelete
            ? `${fmtDate(rateDelete.validityFrom)} - ${fmtDate(rateDelete.validityTo)} will be soft-deleted. Quotes already issued are unaffected.`
            : ''
        }
        confirmLabel="Delete"
        tone="destructive"
        onConfirm={async () => {
          if (!rateDelete) return;
          await deleteRate.mutateAsync(rateDelete.id);
          setRateDelete(null);
        }}
      />
    </div>
  );
};
